// Modo "Recrear video" — pantalla propia (ruta /recrear-video),
// ADR-2026-10-09-recrear-video-adentro-de-recrear.
//
// La mitad de video de Recrear (inspirada en Genjutsu de Higgsfield): el usuario pone un
// video de referencia, las imágenes de lo que quiere en él (persona, producto, prenda) y
// unas indicaciones opcionales, y Atlas lo vuelve a hacer. Dos maneras:
//   • Cambiar personaje (`copiar`): mismo movimiento y cámara, con lo de las imágenes.
//   • Cambiar un objeto (`editar`): el mismo video, cambia sólo lo de las imágenes.
//
// Se entra desde Recrear con el interruptor en Video —la card "Recreá tu propio video" o
// una plantilla— y llega con eso puesto por `location.state`. También se abre por URL y
// desde el chat (`abrir_modo`).
//
// Desktop: el shell de los modos (`ShellModoRedisenado`). El panel lleva la configuración;
// la columna de la derecha es Plantillas | Historial —las tres plantillas para cambiar de
// una a otra sin volver, y lo que el modo ya generó—. Al generar salta a Historial.
// Mobile: una sola pantalla con el mismo panel y el Generar fijo abajo; al generar, la
// pantalla de carga y después el video en Creaciones, como Animar imagen.
//
// Colores en hex con alpha (`bg-[#1644EB]/10`): los tokens `brand-*` no aceptan
// modificador de opacidad en este Tailwind y esa clase no genera CSS.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  Clapperboard,
  ImageUp,
  Loader2,
  Package,
  Plus,
  Sparkles,
  AlertTriangle,
  PenLine,
  User,
  X,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useRegistrarPantalla } from '../../../chat-atlas/PuenteDePantallaContext';
import type { ControladorDePantalla } from '../../../chat-atlas/puenteDePantalla';
import { describirRecrearVideo } from '../puente/camposDeModo';
import { modelIdDeAdjunto } from '../adjuntosDelMensaje';
import { empresaRecordada, recordarEmpresa } from '../empresaDeLaSesion';
import { useSidebar } from '../../../context/SidebarContext';
import { useVolverAtras } from '../../../hooks/useVolverAtras';
import { useRubroMarcaActiva } from '../../../hooks/useRubroMarca';
import { rubroVePlantillas, rubroVePlantillasBelleza } from '../../../lib/rubro';
import { useCuentaShell } from '../../estilos/EstiloMinimal';
import LoadingAnimation from '../../../components/LoadingAnimation';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import OptionSheet from '../../../components/opciones/OptionSheet';
import { CreditBadge } from '../../../components/CreditBadge';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import { uploadReferenceImage, type ChatAttachment } from '../agentAttachments';
import { ModalDeFuentes, type IdDeFuente } from '../../../chat-atlas/ui/ModalDeFuentes';
import { fetchUserProducts } from '../productsApi';
import type { MockProductV2 } from '../mockData';
import { ProductsContext, ProductsLoadedContext } from '../productsContext';
import { PickProductPanel } from '../components/PickProductPanel';
import { PanelLateralModo } from '../components/PanelLateralModo';
import { nombreCortoModo } from '../modesCatalog';
import { MAX_TANDAS_EN_CURSO } from '../generacionesEnCurso';
import { ShellModoRedisenado } from './shared/ShellModoRedisenado';
import { useColumnaResultadosModo } from './shared/useColumnaResultadosModo';
import {
  DURACION_MAX_S,
  DURACION_MIN_S,
  MAX_IMAGENES,
  MAX_VIDEO_MB,
  MODO_UTILIZADO,
  ROLES_DE_IMAGEN,
  RUTA_RECREAR_VIDEO,
  creditosDeRecrearVideo,
  duracionDeSalida,
  etiquetaDeRol,
  revisarDuracion,
  rolInicial,
  type EstadoDeRecrearVideo,
  type ImagenDeRecrear,
  type RolDeImagen,
  type VideoDeReferencia,
} from './recrearVideo/recrearVideo';
import { TarjetaDeReferencia } from './redes/TarjetaDeReferencia';
import {
  PLANTILLAS_DE_RECREAR_VIDEO,
  plantillaPorId,
  type PlantillaDeRecrearVideo,
} from './recrearVideo/plantillas';
import {
  errorDePeso,
  medirVideoDeArchivo,
  pedirRecrearVideo,
  subirVideoPropio,
} from './recrearVideo/generarRecrearVideo';
import { CardPlantillaDeVideo, CardSubirVideoParaRecrear } from './recrearVideo/CardsDeRecrearVideo';

const PATH_MODO = 'recrear-video';
const NOMBRE_RESPALDO = 'Recrear video';
/** Indicaciones: el mismo tope que la descripción de Animar imagen. */
const MAX_INDICACIONES = 800;
/** Lo que espera la pantalla de carga de mobile antes de dar el video por listo: un video
 *  recreado tarda de 6 a 15 minutos (ADR-2026-10-16-recrear-video-con-nuestra-receta). */
const CARGA_MS = 540_000;

type Pestana = 'plantillas' | 'historial';

/** El video de una plantilla, como video puesto en el panel. */
const videoDePlantilla = (p: PlantillaDeRecrearVideo): VideoDeReferencia => ({
  origen: 'plantilla',
  url: p.videoUrl,
  duracionSeg: p.duracionSeg,
  proporcion: 9 / 16,
  nombre: p.titulo,
});

const segundos = (n: number) => `${Math.round(n)} s`;

export default function RecrearVideoMode() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth, setMobileBackOverride, setMobileHideBottomNav } = useSidebar();
  const volverAtras = useVolverAtras();
  const { uiNueva } = useCuentaShell();
  const { rubro, marcaId, loaded: rubroCargado } = useRubroMarcaActiva();

  // ── Cuenta ────────────────────────────────────────────────────────────────
  const [companyId, setCompanyId] = useState<string | null>(() => empresaRecordada());
  const [brandId, setBrandId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      setUserId(auth.user.id);
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company, current_brand_id')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (cancelado) return;
      const perfil = p as { id_company?: string | null; current_brand_id?: string | null } | null;
      setCompanyId(perfil?.id_company ?? null);
      setBrandId(perfil?.current_brand_id ?? null);
      recordarEmpresa(perfil?.id_company, auth.user.id);
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  // ── Productos (para "De mi catálogo") ─────────────────────────────────────
  // `PickProductPanel` los lee del context, que esta pantalla provee porque vive afuera
  // de `InterfazV2` (el mismo arreglo de Recrear imagen). Los de la marca activa.
  const [productos, setProductos] = useState<MockProductV2[]>([]);
  const [productosCargados, setProductosCargados] = useState(false);
  useEffect(() => {
    if (!companyId || !rubroCargado) return;
    let cancelado = false;
    fetchUserProducts(companyId, marcaId)
      .then((p) => {
        if (!cancelado) setProductos(p);
      })
      .catch(() => {
        /* sin catálogo queda subir */
      })
      .finally(() => {
        if (!cancelado) setProductosCargados(true);
      });
    return () => {
      cancelado = true;
    };
  }, [companyId, marcaId, rubroCargado]);

  // ── Lo que llega desde Recrear ────────────────────────────────────────────
  const entrada = useRef((location.state as EstadoDeRecrearVideo | null) ?? null).current;
  const plantillaDeEntrada = plantillaPorId(entrada?.plantillaId);

  const [plantilla, setPlantilla] = useState<PlantillaDeRecrearVideo | null>(plantillaDeEntrada);
  const [video, setVideo] = useState<VideoDeReferencia | null>(
    entrada?.videoSubido ?? (plantillaDeEntrada ? videoDePlantilla(plantillaDeEntrada) : null),
  );
  const [indicaciones, setIndicaciones] = useState('');
  const [imagenes, setImagenes] = useState<ImagenDeRecrear[]>([]);
  const [aviso, setAviso] = useState<string | null>(() => {
    const d = entrada?.videoSubido?.duracionSeg ?? 0;
    const r = revisarDuracion(d);
    return r.accion === 'recortar' ? r.aviso : null;
  });
  const [pestana, setPestana] = useState<Pestana>('plantillas');

  const [subiendoVideo, setSubiendoVideo] = useState(false);
  const [subiendoImagen, setSubiendoImagen] = useState(false);
  /** El rol con el que entra la próxima imagen (el del lugar que pide la plantilla). */
  const rolPendienteRef = useRef<RolDeImagen | null>(null);
  /** Qué lugar de la grilla tiene abierto el menú de sumar (Subir / De mi catálogo). */
  const [menuDe, setMenuDe] = useState<string | null>(null);
  const [panelCatalogo, setPanelCatalogo] = useState(false);
  const [fuentesAbiertas, setFuentesAbiertas] = useState<IdDeFuente | null>(null);
  const [conIndicaciones, setConIndicaciones] = useState(false);
  const [videoExpandido, setVideoExpandido] = useState(false);
  const [panelPlantillas, setPanelPlantillas] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generando, setGenerando] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [completeNow, setCompleteNow] = useState(false);
  const pendingNavRef = useRef<string[] | null>(null);
  const [limitModal, setLimitModal] = useState<'free-trial-ended' | 'plan-limit-reached' | null>(null);
  const planRef = useRef<string | null>(null);

  const inputVideoRef = useRef<HTMLInputElement>(null);
  const inputImagenRef = useRef<HTMLInputElement>(null);
  const seqVideoRef = useRef(0);

  // Siempre sale en 1080 (se genera a 480p y se escala): no hay resolución para elegir.
  const costo = video ? creditosDeRecrearVideo(video.duracionSeg) : 0;
  const segundosDeSalida = video ? duracionDeSalida(video.duracionSeg) : 0;
  const lugaresPendientes = useMemo(() => {
    // Lo que la plantilla pide y todavía no está: un lugar por rol que falte.
    if (!plantilla) return [];
    const usados = new Map<RolDeImagen, number>();
    for (const img of imagenes) usados.set(img.rol, (usados.get(img.rol) ?? 0) + 1);
    return plantilla.pide.filter((p) => {
      const n = usados.get(p.rol) ?? 0;
      if (n > 0) {
        usados.set(p.rol, n - 1);
        return false;
      }
      return true;
    });
  }, [plantilla, imagenes]);

  const falta: string | null = !video
    ? 'Subí el video de referencia o elegí una plantilla.'
    : imagenes.length === 0
      ? 'Sumá al menos una imagen de lo que querés en el video.'
      : null;

  // ── Agente ────────────────────────────────────────────────────────────────
  useRegistrarPantalla(
    useMemo<ControladorDePantalla>(
      () => ({
        clave: PATH_MODO,
        describir: () =>
          describirRecrearVideo({
            ruta: RUTA_RECREAR_VIDEO,
            video: video
              ? {
                  segundos: Math.round(video.duracionSeg),
                  deUnaPlantilla: plantilla?.titulo ?? null,
                }
              : null,
            imagenes: imagenes.map((i) => etiquetaDeRol(i.rol)),
            maxImagenes: MAX_IMAGENES,
            indicaciones,
            maxIndicaciones: MAX_INDICACIONES,
            costo: video ? costo : null,
            segundosDeSalida,
            plantillas: PLANTILLAS_DE_RECREAR_VIDEO.map((p) => p.titulo),
          }),
        aplicar: (cambios) => {
          const aplicados: string[] = [];
          for (const c of cambios) {
            if (c.campo === 'indicaciones' && typeof c.valor === 'string') {
              setIndicaciones(c.valor.slice(0, MAX_INDICACIONES));
            } else continue;
            aplicados.push(c.campo);
          }
          return { aplicados, fallidos: [] };
        },
      }),
      [video, plantilla, imagenes, indicaciones, costo, segundosDeSalida],
    ),
  );

  // El menú de sumar se cierra con un click afuera o con Escape.
  useEffect(() => {
    if (!menuDe) return;
    const alClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-menu-de-sumar]')) setMenuDe(null);
    };
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuDe(null);
    };
    document.addEventListener('mousedown', alClick);
    document.addEventListener('keydown', alTeclado);
    return () => {
      document.removeEventListener('mousedown', alClick);
      document.removeEventListener('keydown', alTeclado);
    };
  }, [menuDe]);

  // Back del shell en mobile: vuelve a donde estabas.
  useEffect(() => {
    setMobileBackOverride(() => () => volverAtras());
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volverAtras]);
  // Sin la bottom nav: taparía el Generar, que va fijo abajo (como en Recrear imagen).
  useEffect(() => {
    setMobileHideBottomNav(true);
    return () => setMobileHideBottomNav(false);
  }, [setMobileHideBottomNav]);

  // ── Columna derecha: Historial ────────────────────────────────────────────
  const { historial, visorAbierto, lanzarTanda, puedeCrear } = useColumnaResultadosModo({
    companyId,
    brandId,
    modo: PATH_MODO,
    onError: setError,
    onLimitePlan: () =>
      setLimitModal(planRef.current === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached'),
    historial: { modoUtilizado: MODO_UTILIZADO, soloVideos: true },
  });

  // ── Acciones ──────────────────────────────────────────────────────────────
  const elegirPlantilla = (p: PlantillaDeRecrearVideo) => {
    setPlantilla(p);
    setVideo(videoDePlantilla(p));
    setIndicaciones(p.indicaciones ?? '');
    setConIndicaciones(!!p.indicaciones);
    setAviso(null);
    setError(null);
  };

  const quitarVideo = () => {
    seqVideoRef.current++;
    setVideo(null);
    setPlantilla(null);
    setAviso(null);
  };

  const onVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!companyId) {
      setError('No pudimos identificar tu cuenta. Recargá la página.');
      return;
    }
    const pesado = errorDePeso(file);
    if (pesado) {
      setError(pesado);
      return;
    }
    const seq = ++seqVideoRef.current;
    setError(null);
    setSubiendoVideo(true);
    try {
      const medida = await medirVideoDeArchivo(file);
      const revision = revisarDuracion(medida.duracion);
      if (revision.accion === 'bloquear') {
        if (seq === seqVideoRef.current) setError(revision.motivo);
        return;
      }
      const subido = await subirVideoPropio(file, companyId, medida);
      if (seq !== seqVideoRef.current) return; // ganó una selección más nueva
      setVideo(subido);
      // Un video propio no es la plantilla: se suelta, con sus lugares pedidos.
      setPlantilla(null);
      setAviso(revision.accion === 'recortar' ? revision.aviso : null);
    } catch (err) {
      if (seq === seqVideoRef.current) {
        console.error('[RecrearVideoMode] subir video falló', err);
        setError('No se pudo subir el video. Intentá nuevamente.');
      }
    } finally {
      if (seq === seqVideoRef.current) setSubiendoVideo(false);
    }
  };

  const abrirSubirImagen = (rol: RolDeImagen | null) => {
    rolPendienteRef.current = rol;
    setMenuDe(null);
    inputImagenRef.current?.click();
  };

  const abrirCatalogo = (rol: RolDeImagen | null) => {
    rolPendienteRef.current = rol;
    setMenuDe(null);
    setPanelCatalogo(true);
  };

  const onImagenes = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0 || !companyId) return;
    const lugar = MAX_IMAGENES - imagenes.length;
    if (lugar <= 0) {
      setError(`Entran hasta ${MAX_IMAGENES} imágenes.`);
      return;
    }
    const rol = rolPendienteRef.current;
    rolPendienteRef.current = null;
    setError(null);
    setSubiendoImagen(true);
    try {
      const subidas = await Promise.all(
        files.slice(0, lugar).map((f) => uploadReferenceImage(f, companyId)),
      );
      setImagenes((prev) => [
        ...prev,
        ...subidas.map(
          (s, i): ImagenDeRecrear => ({
            id: crypto.randomUUID(),
            origen: 'subida',
            // El rol del lugar que se tocó va a la primera; el resto, el de siempre.
            rol: i === 0 && rol ? rol : rolInicial('subida'),
            storagePath: s.storagePath,
            previewUrl: s.previewUrl,
          }),
        ),
      ]);
      if (files.length > lugar) setError(`Entran hasta ${MAX_IMAGENES} imágenes: sumamos las primeras ${lugar}.`);
    } catch (err) {
      console.error('[RecrearVideoMode] subir imagen falló', err);
      setError(err instanceof Error ? err.message : 'No se pudo subir la imagen.');
    } finally {
      setSubiendoImagen(false);
    }
  };

  const elegirProductos = (elegidos: MockProductV2[]) => {
    setPanelCatalogo(false);
    const rol = rolPendienteRef.current;
    rolPendienteRef.current = null;
    setImagenes((prev) => {
      const yaEstan = new Set(prev.flatMap((i) => (i.origen === 'catalogo' ? [i.productId] : [])));
      const nuevos = elegidos
        .filter((p) => !yaEstan.has(p.id))
        .slice(0, Math.max(0, MAX_IMAGENES - prev.length))
        .map(
          (p, i): ImagenDeRecrear => ({
            id: crypto.randomUUID(),
            origen: 'catalogo',
            rol: i === 0 && rol ? rol : rolInicial('catalogo'),
            productId: p.id,
            nombre: p.name,
            previewUrl: p.imageUrl,
          }),
        );
      return [...prev, ...nuevos];
    });
  };

  const cambiarRol = (id: string, rol: RolDeImagen) =>
    setImagenes((prev) => prev.map((i) => (i.id === id ? { ...i, rol } : i)));
  const quitarImagen = (id: string) => setImagenes((prev) => prev.filter((i) => i.id !== id));

  const generar = async () => {
    if (!video || !userId || !companyId || generando || falta) return;
    const enPanel = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;
    setGenerando(true);
    setError(null);
    try {
      // Guard con el dato fresco: los créditos tienen que alcanzar (es para todos los planes).
      const { data: perfil } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes:imagenes_disponibles')
        .eq('id_company', companyId)
        .single();
      const plan = (perfil as { plan?: string } | null)?.plan;
      planRef.current = plan ?? null;
      const restantes = (perfil as { imagenes_restantes?: number | null } | null)?.imagenes_restantes;
      if (typeof restantes === 'number' && restantes < costo) {
        setGenerando(false);
        setLimitModal(plan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached');
        return;
      }
      if (enPanel && !puedeCrear()) {
        setGenerando(false);
        setError(`Ya hay ${MAX_TANDAS_EN_CURSO} generaciones en curso. Esperá a que termine alguna para pedir otra.`);
        return;
      }

      const pedido = pedirRecrearVideo({ companyId, video, plantillaId: plantilla?.id ?? null, imagenes, indicaciones });

      if (enPanel) {
        // La tanda va a Historial y el panel queda listo para el próximo.
        lanzarTanda({
          total: 1,
          esVideo: true,
          ratio: video.proporcion,
          creditos: costo,
          duracionEstimadaMs: pedido.duracionEstimadaMs,
          resultado: pedido.resultado,
          errorGenerico: 'No se pudo recrear el video. Intentá nuevamente.',
        });
        setPestana('historial');
        setGenerando(false);
        return;
      }

      setShowLoading(true);
      setCompleteNow(false);
      const ids = await pedido.resultado;
      pendingNavRef.current = ids;
      setCompleteNow(true);
    } catch (e) {
      console.error('[RecrearVideoMode] generar falló', e);
      setShowLoading(false);
      setGenerando(false);
      if (e instanceof GenerateAdError && e.errorType === 'billing') {
        setLimitModal(planRef.current === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached');
      } else {
        setError(
          e instanceof GenerateAdError && e.isActionable
            ? e.message
            : 'No se pudo recrear el video. Intentá nuevamente.',
        );
      }
    }
  };

  // ── Piezas ────────────────────────────────────────────────────────────────

  const errorBox = error ? (
    <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">{error}</div>
  ) : null;

  // ── Cards estilo Recrear ────────────────────────────────────────────────

  /** Card-row genérica: thumbnail (o ícono), título, subtítulo, chevron. */
  const cardRow = (opts: {
    thumbnail?: React.ReactNode;
    icono?: React.ReactNode;
    titulo: string;
    subtitulo: string;
    onClick: () => void;
    extra?: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={opts.onClick}
      className="w-full h-[76px] flex items-center gap-3 rounded-xl bg-[#00000009] px-3 pr-3.5 text-left transition-colors hover:bg-[#ECEDF1] active:scale-[0.995]"
    >
      {opts.thumbnail ?? (
        <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
          {opts.icono}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold text-brand-black leading-tight">{opts.titulo}</p>
        <p className="text-[13px] text-[#3C3C43]/55 mt-0.5 leading-snug">{opts.subtitulo}</p>
      </div>
      {opts.extra}
      <ChevronRight className="w-4 h-4 shrink-0 text-brand-black/80" />
    </button>
  );

  // Separamos las imágenes por categoría: producto/prenda vs persona.
  const imagenesProducto = imagenes.filter((i) => i.rol === 'producto' || i.rol === 'prenda' || i.rol === 'otro');
  const imagenesPersona = imagenes.filter((i) => i.rol === 'persona');

  const miniCard = (img: ImagenDeRecrear) => (
    <div key={img.id} className="relative aspect-square overflow-hidden rounded-xl bg-neutral-100 border border-neutral-200">
      <img src={img.previewUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <button
        type="button"
        onClick={() => quitarImagen(img.id)}
        aria-label="Quitar imagen"
        className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm hover:bg-black/70"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );

  // ── Card: Video de referencia ──
  const cardVideo = video ? (
    <div>
      {cardRow({
        thumbnail: (
          <video
            key={video.url}
            src={video.url}
            muted
            loop
            autoPlay
            playsInline
            className="h-[52px] w-[52px] shrink-0 rounded-lg bg-neutral-900 object-cover"
          />
        ),
        titulo: plantilla ? 'Plantilla' : 'Video',
        subtitulo: plantilla ? plantilla.titulo : (video.nombre || 'Tu video'),
        onClick: () => setPanelPlantillas(true),
      })}
      {aviso && (
        <p className="mt-2 inline-flex items-start gap-1.5 text-[12.5px] text-[#8A5A00] leading-snug px-1">
          <AlertTriangle className="w-3.5 h-3.5 mt-[1px] shrink-0" />
          {aviso}
        </p>
      )}
    </div>
  ) : (
    <button
      type="button"
      onClick={() => inputVideoRef.current?.click()}
      disabled={subiendoVideo}
      className="w-full h-[76px] flex items-center gap-3 rounded-xl bg-[#00000009] px-3 pr-3.5 text-left transition-colors hover:bg-[#ECEDF1] active:scale-[0.995] disabled:opacity-60"
    >
      <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-lg bg-white">
        {subiendoVideo ? (
          <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
        ) : (
          <Clapperboard className="w-5 h-5 text-[#3C3C43]/40" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold text-brand-black leading-tight">
          {subiendoVideo ? 'Subiendo…' : 'Video'}
        </p>
        <p className="text-[13px] text-[#3C3C43]/55 mt-0.5 leading-snug">Elegí una plantilla o subí el tuyo</p>
      </div>
      <ChevronRight className="w-4 h-4 shrink-0 text-brand-black/80" />
    </button>
  );

  // ── Bloque de referencias — mismo componente que "Crear desde cero" ──
  const adjuntosDeReferencia: ChatAttachment[] = imagenes.map((i) => ({
    kind: 'image' as const,
    id: i.id,
    previewUrl: i.previewUrl,
    storagePath: '',
  }));
  const cardProducto = (
    <TarjetaDeReferencia
      rotulo="Añadir referencia"
      descripcion="Productos, modelos o imágenes"
      tamanoIcono={52}
      Icono={Package}
      adjuntos={adjuntosDeReferencia}
      carpeta={{
        imagenes: [
          '/images/onboarding/generation/carousel/1.jpg',
          '/images/onboarding/generation/carousel/3.jpg',
          '/images/onboarding/generation/carousel/5.jpg',
        ],
        tono: 'azul',
      }}
      onAbrir={() => setFuentesAbiertas('producto')}
      onQuitar={() => setImagenes([])}
    />
  );

  const bloqueIndicaciones = (
    <div className="rounded-xl bg-[#00000009]">
      {/* Fila superior: ícono + texto + switch — mismo alto que las otras cards */}
      <div className="flex h-[76px] items-center gap-3 px-3 pr-3.5">
        <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[12px] bg-white shadow-sm">
          <PenLine className="w-5 h-5 text-[#1644EB]/50" strokeWidth={1.75} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-brand-black leading-tight">Indicaciones</p>
          <p className="text-[13px] text-[#3C3C43]/55 mt-0.5 leading-snug">
            {conIndicaciones ? 'Describí lo que querés' : 'Opcional'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={conIndicaciones}
          onClick={() => {
            setConIndicaciones((v) => {
              if (v) setIndicaciones('');
              return !v;
            });
          }}
          className={`relative inline-flex h-[26px] w-[46px] shrink-0 items-center rounded-full transition-colors duration-200 ${
            conIndicaciones ? 'bg-brand-primary' : 'bg-black/[0.12]'
          }`}
        >
          <span
            className={`inline-block h-[22px] w-[22px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.15)] transition-transform duration-200 ${
              conIndicaciones ? 'translate-x-[22px]' : 'translate-x-[2px]'
            }`}
          />
        </button>
      </div>
      {/* Textarea dentro de la card, debajo de la fila */}
      {conIndicaciones && (
        <div className="mt-3 mx-3 rounded-[13px] bg-white overflow-hidden border border-neutral-200 px-4 py-3 mb-3 animate-paso-adelante">
          <textarea
            rows={3}
            value={indicaciones}
            maxLength={MAX_INDICACIONES}
            onChange={(e) => setIndicaciones(e.target.value)}
            placeholder={plantilla?.ejemplo ?? 'Ej: que sostenga el producto con la mano derecha, con luz cálida…'}
            className="w-full text-[15px] text-brand-black placeholder:text-[#3C3C43]/30 bg-transparent resize-none focus:outline-none leading-relaxed"
          />
        </div>
      )}
    </div>
  );

  const contenidoDelPanel = (
    <div className="flex flex-col gap-2.5">
      {cardVideo}
      {cardProducto}
      {bloqueIndicaciones}
      {errorBox}
    </div>
  );

  const botonGenerar = (
    <button
      type="button"
      onClick={generar}
      disabled={generando || !!falta}
      title={falta ?? undefined}
      className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors"
    >
      <Sparkles className="w-5 h-5" />
      Recrear video
      {video && <CreditBadge credits={costo} tone="on-primary" size="sm" />}
    </button>
  );

  const pie = (clase: string) => (
    <div className={clase}>
      {botonGenerar}
      {falta && <p className="mt-2 text-center text-[12.5px] text-[#3C3C43]/55">{falta}</p>}
    </div>
  );

  const panelDesktop = (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-5 pb-4">{contenidoDelPanel}</div>
      {pie('shrink-0 px-6 pt-3 pb-4 bg-white border-t border-black/[0.05]')}
    </>
  );

  const pestanas = (
    <div className="shrink-0 flex justify-center pb-4">
      <div role="tablist" className="inline-flex gap-1 rounded-[12px] bg-[#EEEFF3] p-1">
        {(['plantillas', 'historial'] as const).map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={pestana === p}
            onClick={() => setPestana(p)}
            className={`rounded-[9px] px-4 py-1.5 text-[14px] font-medium transition-[background-color,box-shadow,color] ${
              pestana === p
                ? 'bg-white text-brand-black shadow-[0_1px_3px_rgba(16,24,40,0.12)]'
                : 'text-[#3C3C43]/60 hover:text-brand-black'
            }`}
          >
            {p === 'plantillas' ? 'Plantillas' : 'Historial'}
          </button>
        ))}
      </div>
    </div>
  );

  const grillaDePlantillas = (columnas: string, cerrarPanel = false) => (
    <div className={`grid ${columnas} gap-3`}>
      {PLANTILLAS_DE_RECREAR_VIDEO.map((p) => (
        <CardPlantillaDeVideo
          key={p.id}
          plantilla={p}
          elegida={plantilla?.id === p.id}
          onClick={() => {
            elegirPlantilla(p);
            if (cerrarPanel) setPanelPlantillas(false);
          }}
        />
      ))}
    </div>
  );

  const muestra = (
    <div className="flex-1 min-h-0 flex flex-col">
      {video ? (
        <>
          {pestanas}
          {pestana === 'plantillas' ? (
            /* Plantilla o video seleccionado: lo muestra grande, centrado. */
            <div className="flex-1 min-h-0 flex items-center justify-center p-6">
              <div className="w-full max-w-[360px]">
                <video
                  key={video.url}
                  src={video.url}
                  muted
                  loop
                  autoPlay
                  playsInline
                  className="w-full rounded-2xl bg-neutral-900 object-cover shadow-lg"
                  style={{ aspectRatio: video.proporcion ? `${video.proporcion}` : '9/16' }}
                />
                {plantilla && (
                  <div className="mt-4 text-center">
                    <p className="text-[17px] font-semibold text-brand-black">{plantilla.titulo}</p>
                    <p className="text-[14px] text-[#3C3C43]/60 mt-1">{plantilla.bajada}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            historial
          )}
        </>
      ) : (
        /* Sin video: la grilla de plantillas para elegir una. */
        <div key="plantillas" className="flex-1 min-h-0 overflow-y-auto animate-paso-adelante">
          <div className="mx-auto max-w-[720px]">
            <div className="text-center mb-5">
              <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
                Convertí un video en muchos
              </h2>
              <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
                Tomá el movimiento de un video y hacelo de nuevo con tu persona, tu producto o tu ropa.
                Elegí una plantilla o subí el tuyo.
              </p>
            </div>
            {grillaDePlantillas('grid-cols-3')}
          </div>
        </div>
      )}
    </div>
  );

  // Las migas: dentro de Recrear (donde existe para el rubro), y si no, desde Inicio.
  const rutaRecrear = rubroVePlantillasBelleza(rubro)
    ? '/interfaz-v2/plantillas-belleza'
    : rubroVePlantillas(rubro)
      ? '/interfaz-v2/plantillas'
      : null;
  const nombreModo = nombreCortoModo(PATH_MODO, NOMBRE_RESPALDO);
  const migas = [
    { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
    ...(rutaRecrear
      ? [
          {
            label: nombreCortoModo('plantillas', 'Recrear', { uiNueva }),
            onClick: () => navigate(`${rutaRecrear}?media=video`),
          },
        ]
      : []),
    { label: nombreModo },
  ];

  const inputs = (
    <>
      <input ref={inputVideoRef} type="file" accept="video/*" className="hidden" onChange={onVideo} />
      <input ref={inputImagenRef} type="file" accept="image/*" multiple className="hidden" onChange={onImagenes} />
    </>
  );

  const pickerProductos = (variant: 'desktop' | 'mobile') => (
    <PickProductPanel
      variant={variant}
      hideBack
      hideHeader
      initialSelectedIds={imagenes.flatMap((i) => (i.origen === 'catalogo' ? [i.productId] : []))}
      onConfirm={elegirProductos}
      onBack={() => setPanelCatalogo(false)}
      rubro={rubro}
      confirmLabel="Usar estos productos"
      footerSiempreVisible
      columnasFijas={variant === 'desktop' ? 3 : 2}
      nombreSobreLaFoto
      ocultarBloqueSubida
    />
  );

  return (
    <ProductsContext.Provider value={productos}>
      <ProductsLoadedContext.Provider value={productosCargados}>
        {inputs}
        <ModalDeFuentes
          abierto={!!fuentesAbiertas}
          onCerrar={() => setFuentesAbiertas(null)}
          multiple
          adjuntosActuales={imagenes.map((i): ChatAttachment => ({
            kind: 'image',
            id: i.id,
            previewUrl: i.previewUrl,
            storagePath: '',
          }))}
          onQuitar={(id) => quitarImagen(id)}
          onAdjuntar={(adjunto) => {
            const url =
              adjunto.kind === 'image' ? adjunto.previewUrl
              : adjunto.kind === 'product' ? adjunto.imageUrl
              : adjunto.kind === 'model' ? (adjunto.imageUrl ?? null)
              : adjunto.kind === 'referencia' ? adjunto.imageUrl
              : null;
            if (!url) return;
            // Evitar duplicados
            if (imagenes.some((i) => i.id === adjunto.id)) return;
            const rol: RolDeImagen =
              adjunto.kind === 'model' ? 'persona'
              : adjunto.kind === 'product' ? 'producto'
              : adjunto.kind === 'referencia' ? 'otro'
              : rolPendienteRef.current ?? 'producto';
            const nombre =
              adjunto.kind === 'product' ? adjunto.name
              : adjunto.kind === 'model' ? adjunto.name
              : adjunto.kind === 'referencia' ? adjunto.name
              : '';
            if (adjunto.kind === 'product') {
              setImagenes((prev) => [
                ...prev,
                {
                  id: adjunto.id,
                  previewUrl: url,
                  rol,
                  origen: 'catalogo' as const,
                  productId: adjunto.productId,
                  nombre,
                },
              ]);
            } else if (adjunto.kind === 'image' && adjunto.storagePath) {
              // Una foto subida: se firma al generar.
              setImagenes((prev) => [
                ...prev,
                { id: adjunto.id, previewUrl: url, rol, origen: 'subida' as const, storagePath: adjunto.storagePath },
              ]);
            } else {
              // Una modelo guardada o una referencia de la biblioteca: viajan con su link de
              // nuestro Storage. Antes se cargaban como "subidas" sin archivo y nunca llegaban.
              const modeloId = adjunto.kind === 'model' ? Number(modelIdDeAdjunto(adjunto)) || undefined : undefined;
              setImagenes((prev) => [...prev, { id: adjunto.id, previewUrl: url, rol, origen: 'url' as const, url, ...(modeloId ? { modeloId } : {}) }]);
            }
          }}
        />

        {/* ── MOBILE: una pantalla con el panel y el Generar fijo abajo ── */}
        <div className="md:hidden flex min-h-[100dvh] flex-col bg-surface-mobile">
          {/* `pb-44`: el aire del pie fijo, para que lo último del panel no quede abajo. */}
          <div className="flex-1 px-5 pt-4 pb-44">
            <h1 className="text-[24px] font-semibold leading-tight text-brand-black">{nombreModo}</h1>
            <p className="text-[14.5px] text-[#3C3C43]/60 mt-1 mb-5 leading-snug">
              Subí un video o elegí una plantilla, y sumá lo que querés en él.
            </p>
            {!video && (
              <div className="-mx-5 mb-6 overflow-x-auto px-5">
                <div className="flex gap-3 w-max">
                  {PLANTILLAS_DE_RECREAR_VIDEO.map((p) => (
                    <div key={p.id} className="w-[150px]">
                      <CardPlantillaDeVideo plantilla={p} onClick={() => elegirPlantilla(p)} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {contenidoDelPanel}
          </div>
          {/* `fixed` y no `sticky`: el `main` del shell tiene `overflow-x-hidden`, que lo
              vuelve el contenedor del sticky sin que scrollee, y el pie quedaba al final. */}
          {pie(
            'fixed inset-x-0 bottom-0 z-20 bg-white px-5 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] border-t border-black/[0.06]',
          )}
        </div>

        {/* ── DESKTOP ── */}
        <ShellModoRedisenado
          sidebarWidth={sidebarWidth}
          migas={migas}
          titulo={nombreModo}
          panel={panelDesktop}
          panelLateral={({ contenedorRef, offsetIzquierdo }) => (
            <>
              <PanelLateralModo
                abierto={panelPlantillas}
                onCerrar={() => setPanelPlantillas(false)}
                titulo="Elegí una plantilla"
                maxAncho={615}
                offsetIzquierdo={offsetIzquierdo}
                contenedorRef={contenedorRef}
              >
                <div className="flex-1 min-h-0 overflow-y-auto p-4">
                  <div className="grid grid-cols-3 gap-3">
                    {/* Primera card: subir video propio */}
                    <CardSubirVideoParaRecrear
                      aspectClass="aspect-[9/16]"
                      subiendo={subiendoVideo}
                      error={null}
                      onClick={() => {
                        setPanelPlantillas(false);
                        inputVideoRef.current?.click();
                      }}
                    />
                    {/* Plantillas */}
                    {PLANTILLAS_DE_RECREAR_VIDEO.map((p) => (
                      <CardPlantillaDeVideo
                        key={p.id}
                        plantilla={p}
                        elegida={plantilla?.id === p.id}
                        onClick={() => {
                          elegirPlantilla(p);
                          setPanelPlantillas(false);
                        }}
                      />
                    ))}
                  </div>
                </div>
              </PanelLateralModo>
              <PanelLateralModo
                abierto={panelCatalogo}
                onCerrar={() => setPanelCatalogo(false)}
                titulo="Elegí de tu catálogo"
                offsetIzquierdo={offsetIzquierdo}
                contenedorRef={contenedorRef}
              >
                <div className="flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:min-h-0">
                  {pickerProductos('desktop')}
                </div>
              </PanelLateralModo>
            </>
          )}
          // Sin `muestraAlBorde`: la columna lleva arriba el interruptor Plantillas |
          // Historial, y llevarla al marco en una pestaña y no en la otra lo hacía saltar.
          cederZIndex={visorAbierto}
          muestra={muestra}
        />

        {/* El catálogo en el teléfono: el sheet de opciones. */}
        <div className="md:hidden">
          <OptionSheet open={panelCatalogo} onClose={() => setPanelCatalogo(false)} title="Elegí de tu catálogo" hideAccept>
            {pickerProductos('mobile')}
          </OptionSheet>
        </div>

        {/* Plantillas en el teléfono: el sheet para cambiar de video/plantilla. */}
        <div className="md:hidden">
          <OptionSheet open={panelPlantillas} onClose={() => setPanelPlantillas(false)} title="Elegí una plantilla" hideAccept>
            <div className="grid grid-cols-2 gap-3 p-4">
              <CardSubirVideoParaRecrear
                aspectClass="aspect-[9/16]"
                subiendo={subiendoVideo}
                error={null}
                onClick={() => {
                  setPanelPlantillas(false);
                  inputVideoRef.current?.click();
                }}
              />
              {PLANTILLAS_DE_RECREAR_VIDEO.map((p) => (
                <CardPlantillaDeVideo
                  key={p.id}
                  plantilla={p}
                  elegida={plantilla?.id === p.id}
                  onClick={() => {
                    elegirPlantilla(p);
                    setPanelPlantillas(false);
                  }}
                />
              ))}
            </div>
          </OptionSheet>
        </div>

        {showLoading && (
          <LoadingAnimation
            message="Recreando el video…"
            subMessage="Esto puede tardar unos minutos."
            durationMs={CARGA_MS}
            completeNow={completeNow}
            onCompleted={() => {
              setShowLoading(false);
              setGenerando(false);
              const ids = pendingNavRef.current;
              pendingNavRef.current = null;
              if (ids && ids.length > 0 && companyId) {
                navigate('/redes-sociales/imagenes-generadas', { state: { imageIds: ids, companyId } });
              }
            }}
          />
        )}
        <SubscriptionLimitModal
          isOpen={limitModal !== null}
          type={limitModal ?? 'plan-limit-reached'}
          onClose={() => setLimitModal(null)}
        />
      </ProductsLoadedContext.Provider>
    </ProductsContext.Provider>
  );
}
